'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

const DAY_HEADERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTH_NAMES_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

interface Props {
  referenceDate: Date;
  today: Date;
  onSelectMonth: (year: number, month: number) => void;
  onClose: () => void;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  let d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

interface MiniMonthGridProps {
  year: number;
  month: number;
  today: Date;
  onClick: () => void;
}

function MiniMonthGrid({ year, month, today, onClick }: MiniMonthGridProps) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOffset = getFirstDayOfMonth(year, month);

  const cells: { day: number | null }[] = [];
  for (let i = 0; i < firstDayOffset; i++) cells.push({ day: null });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d });

  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i++) cells.push({ day: null });
  }

  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();
  const isCurrentMonth = year === todayYear && month === todayMonth;

  return (
    <button
      onClick={onClick}
      className="w-full h-full text-left rounded-xl border border-border/40 hover:border-primary/50 hover:bg-primary/5 transition-all duration-150 p-2 cursor-pointer group"
      aria-label={`Go to ${MONTH_NAMES_FULL[month]} ${year}`}
    >
      {/* Month header */}
      <div className="flex items-center justify-center gap-1.5 mb-1.5">
        <span className="font-display text-[10px] sm:text-xs text-primary tracking-widest uppercase font-semibold group-hover:text-primary">
          {MONTH_NAMES_FULL[month]}
        </span>
        <span className="font-display text-[9px] text-muted-foreground tracking-wider">{year}</span>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0 mb-0.5">
        {DAY_HEADERS.map((h, i) => (
          <div
            key={`msel-hdr-${month}-${i}`}
            className="text-center font-display text-[7px] text-muted-foreground/50 tracking-wider uppercase font-bold"
          >
            {h}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0">
        {cells.map((cell, idx) => {
          if (!cell.day) {
            return <div key={`msel-cell-${month}-${idx}`} className="aspect-square" />;
          }
          const isToday = isCurrentMonth && cell.day === todayDay;
          return (
            <div
              key={`msel-cell-${month}-${idx}`}
              className={`
                aspect-square flex items-center justify-center rounded-full text-[7px] font-display
                ${isToday ? 'bg-primary/20 text-primary' : 'text-foreground/60'}
              `}
            >
              {cell.day}
            </div>
          );
        })}
      </div>
    </button>
  );
}

/** Offset a month by `delta` months from (baseYear, baseMonth) */
function offsetMonth(baseYear: number, baseMonth: number, delta: number): { year: number; month: number } {
  let d = new Date(baseYear, baseMonth + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

const BATCH = 8; // months to load per batch

export default function MonthSelectorView({ referenceDate, today, onSelectMonth, onClose }: Props) {
  const refYear = referenceDate.getFullYear();
  const refMonth = referenceDate.getMonth();

  // offsets relative to referenceDate: negative = past, positive = future
  const [startOffset, setStartOffset] = useState(-BATCH);
  const [endOffset, setEndOffset] = useState(BATCH);

  // Guard: ignore clicks/taps for a short period after mount to prevent
  // Android ghost-click (the touchend that opened this popup firing on a month button)
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const initialScrollDone = useRef(false);

  // Build the list of months to render
  const months: { year: number; month: number; offset: number }[] = [];
  for (let i = startOffset; i <= endOffset; i++) {
    const { year, month } = offsetMonth(refYear, refMonth, i);
    months.push({ year, month, offset: i });
  }

  // Scroll to the reference month (offset 0) on first render
  useEffect(() => {
    if (initialScrollDone.current) return;
    const container = scrollRef.current;
    if (!container) return;

    // Find the element for offset 0
    const refEl = container.querySelector('[data-offset="0"]') as HTMLElement | null;
    if (refEl) {
      refEl.scrollIntoView({ block: 'start' });
      initialScrollDone.current = true;
    }
  });

  // Infinite scroll: load more months when sentinels are visible
  const loadMore = useCallback((direction: 'up' | 'down') => {
    if (direction === 'up') {
      setStartOffset(prev => prev - BATCH);
    } else {
      setEndOffset(prev => prev + BATCH);
    }
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    const topEl = topSentinelRef.current;
    const bottomEl = bottomSentinelRef.current;
    if (!container || !topEl || !bottomEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          if (entry.target === topEl) loadMore('up');
          if (entry.target === bottomEl) loadMore('down');
        });
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(topEl);
    observer.observe(bottomEl);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Select month"
    >
      <div
        className="relative w-full max-w-sm mx-4 bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden animate-modal-in flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/40 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-primary text-sm animate-gold-pulse select-none" aria-hidden>✦</span>
            <h2 className="font-decorative text-sm text-primary tracking-widest uppercase">Select Month</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-primary transition-colors duration-200 text-xl leading-none p-1 rounded"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable month grid — infinite scroll */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-arcane px-4 py-4"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}>
          {/* Top sentinel — triggers loading past months */}
          <div ref={topSentinelRef} className="h-1" />

          <div className="grid grid-cols-2 gap-3 items-stretch">
            {months.map(({ year, month, offset }) => (
              <div key={`msel-${year}-${month}`} data-offset={offset}>
                <MiniMonthGrid
                  year={year}
                  month={month}
                  today={today}
                  onClick={() => ready && onSelectMonth(year, month)}
                />
              </div>
            ))}
          </div>

          {/* Bottom sentinel — triggers loading future months */}
          <div ref={bottomSentinelRef} className="h-1" />
        </div>
      </div>
    </div>
  );
}
